import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { apiClient } from '@/services/api';
import { financeService } from '@/services/financeService';
import { useAuthStore } from '@/store/authStore';
import type {
  AdminFinancialHealthDTO,
  CourierWalletSummaryDTO,
  HighRiskCourierDTO,
  LedgerTransactionDTO,
  RestaurantFinanceSummaryDTO,
} from '@/types';

function shouldRetryFinanceQuery(error: unknown): boolean {
  const axiosError = error as AxiosError | undefined;
  const status = axiosError?.response?.status;
  if (status === 401 || status === 403) {
    return false;
  }
  return true;
}

function useIsEnabledForRoles(allowedRoles: Array<'USER' | 'ADMIN' | 'COURIER' | 'RESTAURANT'>) {
  const { isAuthenticated, user } = useAuthStore();
  return Boolean(isAuthenticated && user?.role && allowedRoles.includes(user.role));
}

export function useCourierWalletSummary() {
  const enabled = useIsEnabledForRoles(['COURIER', 'ADMIN']);
  return useQuery<CourierWalletSummaryDTO>({
    queryKey: ['finance', 'courier', 'wallet'],
    queryFn: () => financeService.getCourierWalletSummary(),
    staleTime: 10_000,
    enabled,
    retry: (failureCount, error) => failureCount < 2 && shouldRetryFinanceQuery(error),
  });
}

export function useCourierLedger() {
  const enabled = useIsEnabledForRoles(['COURIER', 'ADMIN']);
  return useQuery<{ transactions: LedgerTransactionDTO[] }>({
    queryKey: ['finance', 'courier', 'ledger'],
    queryFn: () => financeService.getCourierLedger(),
    staleTime: 10_000,
    enabled,
    retry: (failureCount, error) => failureCount < 2 && shouldRetryFinanceQuery(error),
  });
}

export function useRestaurantFinanceSummary() {
  const enabled = useIsEnabledForRoles(['RESTAURANT', 'ADMIN']);
  return useQuery<RestaurantFinanceSummaryDTO>({
    queryKey: ['finance', 'restaurant', 'summary'],
    queryFn: () => financeService.getRestaurantFinanceSummary(),
    staleTime: 10_000,
    enabled,
    retry: (failureCount, error) => failureCount < 2 && shouldRetryFinanceQuery(error),
  });
}

export function useRestaurantLedger() {
  const enabled = useIsEnabledForRoles(['RESTAURANT', 'ADMIN']);
  return useQuery<{ transactions: LedgerTransactionDTO[] }>({
    queryKey: ['finance', 'restaurant', 'ledger'],
    queryFn: () => financeService.getRestaurantLedger(),
    staleTime: 10_000,
    enabled,
    retry: (failureCount, error) => failureCount < 2 && shouldRetryFinanceQuery(error),
  });
}

export function useAdminFinancialHealth() {
  const enabled = useIsEnabledForRoles(['ADMIN']);
  return useQuery<AdminFinancialHealthDTO>({
    queryKey: ['finance', 'admin', 'health'],
    queryFn: () => financeService.getAdminFinancialHealth(),
    staleTime: 10_000,
    enabled,
    retry: (failureCount, error) => failureCount < 2 && shouldRetryFinanceQuery(error),
  });
}

export function useHighRiskCouriers() {
  const enabled = useIsEnabledForRoles(['ADMIN']);
  return useQuery<{ couriers: HighRiskCourierDTO[] }>({
    queryKey: ['finance', 'admin', 'highRiskCouriers'],
    queryFn: () => financeService.getHighRiskCouriers(),
    staleTime: 10_000,
    enabled,
    retry: (failureCount, error) => failureCount < 2 && shouldRetryFinanceQuery(error),
  });
}

export function useSuspendCourier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (courierId: string) => {
      await apiClient.post<void>(`/admin/couriers/${courierId}/suspend`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['finance', 'admin', 'highRiskCouriers'] });
    },
  });
}

export function useActivateCourier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (courierId: string) => {
      await apiClient.post<void>(`/admin/couriers/${courierId}/activate`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['finance', 'admin', 'highRiskCouriers'] });
    },
  });
}

export function useSubmitCashPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { amount: number; orderIds?: string[]; notes?: string }) => {
      return await financeService.submitCashPayment(params.amount, params.orderIds, params.notes);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['finance', 'courier', 'wallet'] });
      await queryClient.invalidateQueries({ queryKey: ['finance', 'courier', 'ledger'] });
      await queryClient.invalidateQueries({ queryKey: ['finance', 'courier', 'pendingCash'] });
    },
  });
}

export function useCourierPendingCashPayments() {
  const enabled = useIsEnabledForRoles(['COURIER', 'ADMIN']);
  return useQuery({
    queryKey: ['finance', 'courier', 'pendingCash'],
    queryFn: () => financeService.getPendingCashPaymentsForCourier(),
    staleTime: 10_000,
    enabled,
    retry: (failureCount, error) => failureCount < 2 && shouldRetryFinanceQuery(error),
  });
}

